import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createCanvasRequest,
    createCanvasWithAIRequest,
    deleteCanvasRequest,
    getCanvases,
    renameCanvasRequest,
} from './canvasApi'
import { getWorkspaceID } from '../../../utils'
import { EVENTS, trackEvent } from '../../../events'

const EMPTY_CANVASES = []

export const useCanvases = () => {
    const workspaceID = getWorkspaceID()
    const queryClient = useQueryClient()

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['canvases', workspaceID],
        queryFn: () => getCanvases(),
        enabled: !!workspaceID,
    })

    const { mutate: createCanvas, isPending: isCreating } = useMutation({
        mutationFn: ({ canvasName, onSuccess }) => createCanvasRequest({ canvasName }),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['canvases', workspaceID] }).then(() => {
                if (variables?.onSuccess) variables?.onSuccess(response.canvasID)
            })
            trackEvent(EVENTS.CANVAS.CREATE_BLANK, { canvas: { canvasID: response.canvasID } })
        },
    })

    const { mutate: deleteCanvas, isPending: isDeleting } = useMutation({
        mutationFn: ({ canvasID }) => deleteCanvasRequest({ canvasID }),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['canvases', workspaceID] }).then(() => {
                if (variables?.onSuccess) variables?.onSuccess()
            })
            trackEvent(EVENTS.CANVAS.DELETE, { canvasID: variables.canvasID })
        },
    })

    const { mutate: renameCanvas, isPending: isRenaming } = useMutation({
        mutationFn: ({ canvasID, canvasName }) => renameCanvasRequest({ canvasID, canvasName }),
        onSuccess: (response, variables) => {
            refetch().then(() => {
                if (variables?.onSuccess) variables?.onSuccess()
            })
            trackEvent(EVENTS.CANVAS.RENAME, {
                canvasID: variables.canvasID,
                canvasName: variables.canvasName,
            })
        },
    })

    const { mutate: generateCanvasWithAI, isPending: isCreatingWithAI } = useMutation({
        mutationFn: ({ inboxID, dataDescription, dataQuestion, slideTemplate }) =>
            createCanvasWithAIRequest({ dataDescription, dataQuestion, inboxID, slideTemplate }),
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onError: (error, variables) => {
            variables?.onError(error)
        },
        onSettled: (response, variables) => {
            variables?.onSettled(response)
        },
    })

    // Handle both array response and object with canvases property
    // Use stable empty array reference to prevent infinite re-render loops
    const canvases = Array.isArray(data) ? data : (data?.canvases ?? EMPTY_CANVASES)

    return {
        canvases,
        isLoading,
        refetch,
        createCanvas,
        isCreating,
        deleteCanvas,
        isDeleting,
        renameCanvas,
        isRenaming,
        generateCanvasWithAI,
    }
}
